import { useCallback, useMemo, useState, type ReactNode } from "react";

import { markTourCompleted } from "../../lib/onboarding";
import { ProductTour } from "./ProductTour";
import { TourContext, TOUR_STEPS, type TourContextValue } from "./tourContext";

// Holds product-tour state and renders the overlay (ProductTour) alongside the
// app content. The overlay and the sidebar's highlight ring both read this
// provider's context so they stay in lockstep with the active step.
//
// step === -1 means inactive; 0..TOUR_STEPS.length-1 is the live step index.
export function TourProvider({ children }: { children: ReactNode }) {
  const [step, setStep] = useState(-1);

  const startTour = useCallback(() => {
    setStep(0);
  }, []);

  const nextStep = useCallback(() => {
    setStep((prev) => Math.min(prev + 1, TOUR_STEPS.length - 1));
  }, []);

  const prevStep = useCallback(() => {
    setStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const finishTour = useCallback(() => {
    markTourCompleted();
    setStep(-1);
  }, []);

  const skipTour = useCallback(() => {
    markTourCompleted();
    setStep(-1);
  }, []);

  const value = useMemo<TourContextValue>(() => {
    const isActive = step >= 0;
    return {
      isActive,
      step,
      totalSteps: TOUR_STEPS.length,
      activeTargetId: isActive ? (TOUR_STEPS[step]?.targetId ?? null) : null,
      startTour,
      nextStep,
      prevStep,
      finishTour,
      skipTour,
    };
  }, [step, startTour, nextStep, prevStep, finishTour, skipTour]);

  return (
    <TourContext.Provider value={value}>
      {children}
      <ProductTour />
    </TourContext.Provider>
  );
}
